import { useState, useEffect } from 'react';
import {
  Key,
  Eye,
  EyeOff,
  Save,
  Loader2,
  CheckCircle,
  AlertCircle,
  ExternalLink,
  TestTube2,
  Sparkles,
  MessageSquare,
  CreditCard,
  Send,
  Phone
} from 'lucide-react';
import { authenticatedFetch } from '@/lib/auth';

interface APIService {
  id: string;
  service_name: string;
  settings: Record<string, any>;
  is_active: boolean;
  updated_at: string | null;
}

interface ServiceConfig {
  name: string;
  title: string;
  description: string;
  icon: React.ReactNode;
  docsUrl: string;
  fields: {
    key: string;
    label: string;
    type: 'text' | 'password' | 'select';
    placeholder?: string;
    required?: boolean;
    options?: { value: string; label: string }[];
    helpText?: string;
  }[];
}

const SERVICE_CONFIGS: Record<string, ServiceConfig> = {
  openai: {
    name: 'openai',
    title: 'OpenAI',
    description: 'Brukes for AI-validering og forbedring av studieplaner',
    icon: <Sparkles className="w-5 h-5" />,
    docsUrl: 'https://platform.openai.com/docs/api-reference',
    fields: [
      {
        key: 'api_key',
        label: 'API-nøkkel',
        type: 'password',
        placeholder: 'sk-...',
        required: true,
        helpText: 'Finn din API-nøkkel på platform.openai.com'
      },
      {
        key: 'model',
        label: 'Modell',
        type: 'select',
        options: [
          { value: 'gpt-4-turbo', label: 'GPT-4 Turbo (Anbefalt)' },
          { value: 'gpt-4o', label: 'GPT-4o' },
          { value: 'gpt-4o-mini', label: 'GPT-4o Mini (Raskere/billigere)' },
          { value: 'gpt-4', label: 'GPT-4' }
        ],
        helpText: 'GPT-4 Turbo anbefales for best kvalitet og pålitelighet'
      },
      {
        key: 'max_tokens',
        label: 'Maks tokens',
        type: 'text',
        placeholder: '4096',
        helpText: 'Maksimalt antall tokens per forespørsel'
      }
    ]
  },
  twilio: {
    name: 'twilio',
    title: 'Twilio',
    description: 'SMS-varsler for prøveresultater og attester',
    icon: <MessageSquare className="w-5 h-5" />,
    docsUrl: 'https://www.twilio.com/docs/messaging/services',
    fields: [
      {
        key: 'account_sid',
        label: 'Account SID',
        type: 'text',
        placeholder: 'AC...',
        required: true,
        helpText: 'Finn denne i Twilio Console Dashboard'
      },
      {
        key: 'auth_token',
        label: 'Auth Token',
        type: 'password',
        required: true,
        helpText: 'Hemmelighets-token fra Twilio'
      },
      {
        key: 'messaging_service_sid',
        label: 'Messaging Service SID',
        type: 'text',
        placeholder: 'MG... eller VA...',
        helpText: 'For Norge: Bruk Messaging Service med Alphanumeric Sender ID'
      },
      {
        key: 'sender_id',
        label: 'Sender ID (Alfanumerisk)',
        type: 'text',
        placeholder: 'KFO sms',
        helpText: 'Maks 11 tegn. Vises som avsender på SMS i Norge'
      }
    ]
  },
  vipps: {
    name: 'vipps',
    title: 'Vipps MobilePay',
    description: 'Innlogging med Vipps for kandidatportalen',
    icon: <CreditCard className="w-5 h-5" />,
    docsUrl: 'https://developer.vippsmobilepay.com/',
    fields: [
      {
        key: 'client_id',
        label: 'Client ID',
        type: 'text',
        required: true,
        helpText: 'OAuth2 Client ID fra Vipps'
      },
      {
        key: 'client_secret',
        label: 'Client Secret',
        type: 'password',
        required: true,
        helpText: 'OAuth2 Client Secret fra Vipps'
      },
      {
        key: 'subscription_key',
        label: 'Subscription Key (Ocp-Apim)',
        type: 'password',
        required: true,
        helpText: 'API Management subscription key'
      },
      {
        key: 'msn',
        label: 'Merchant Serial Number (MSN)',
        type: 'text',
        placeholder: '123456',
        required: true,
        helpText: 'Din bedrifts serienummer hos Vipps'
      },
      {
        key: 'redirect_uri',
        label: 'Redirect URI',
        type: 'text',
        placeholder: 'https://...',
        helpText: 'OAuth callback URL (må matche Vipps-konfig)'
      }
    ]
  }
};

export default function APISettings() {
  const [services, setServices] = useState<APIService[]>([]);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState<string | null>(null);
  const [testing, setTesting] = useState<string | null>(null);
  const [globalError, setGlobalError] = useState('');
  const [serviceErrors, setServiceErrors] = useState<Record<string, string>>({});
  const [serviceSuccess, setServiceSuccess] = useState<Record<string, string>>({});
  const [showSecrets, setShowSecrets] = useState<Record<string, boolean>>({});
  const [editedSettings, setEditedSettings] = useState<Record<string, Record<string, any>>>({});

  // SMS test state
  const [testPhoneNumber, setTestPhoneNumber] = useState('');
  const [sendingSms, setSendingSms] = useState(false);

  useEffect(() => {
    fetchServices();
  }, []);

  const fetchServices = async () => {
    try {
      setLoading(true);
      const response = await authenticatedFetch('/api/admin/api-settings');
      const data = await response.json();

      if (data.success) {
        setServices(data.services);
        // Initialize edited settings from fetched data
        const initial: Record<string, Record<string, any>> = {};
        data.services.forEach((s: APIService) => {
          initial[s.service_name] = { ...s.settings };
        });
        setEditedSettings(initial);
      } else {
        setGlobalError(data.message || 'Kunne ikke laste API-innstillinger');
      }
    } catch (err: any) {
      setGlobalError(err.message);
    } finally {
      setLoading(false);
    }
  };

  const clearServiceMessages = (serviceName: string) => {
    setServiceErrors(prev => ({ ...prev, [serviceName]: '' }));
    setServiceSuccess(prev => ({ ...prev, [serviceName]: '' }));
  };

  const handleSave = async (serviceName: string) => {
    try {
      setSaving(serviceName);
      clearServiceMessages(serviceName);

      const service = services.find(s => s.service_name === serviceName);
      if (!service) return;

      const response = await authenticatedFetch(`/api/admin/api-settings/${service.id}`, {
        method: 'PUT',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          settings: editedSettings[serviceName],
          is_active: service.is_active
        })
      });

      const data = await response.json();

      if (data.success) {
        setServiceSuccess(prev => ({ ...prev, [serviceName]: 'Innstillinger lagret' }));
        fetchServices();
        setTimeout(() => clearServiceMessages(serviceName), 3000);
      } else {
        setServiceErrors(prev => ({ ...prev, [serviceName]: data.message || 'Kunne ikke lagre innstillinger' }));
      }
    } catch (err: any) {
      setServiceErrors(prev => ({ ...prev, [serviceName]: err.message }));
    } finally {
      setSaving(null);
    }
  };

  const handleToggleActive = async (serviceName: string) => {
    try {
      const service = services.find(s => s.service_name === serviceName);
      if (!service) return;

      const response = await authenticatedFetch(`/api/admin/api-settings/${service.id}`, {
        method: 'PATCH',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ is_active: !service.is_active })
      });

      const data = await response.json();

      if (data.success) {
        setServiceSuccess(prev => ({ ...prev, [serviceName]: !service.is_active ? 'Aktivert' : 'Deaktivert' }));
        fetchServices();
        setTimeout(() => clearServiceMessages(serviceName), 3000);
      } else {
        setServiceErrors(prev => ({ ...prev, [serviceName]: data.message || 'Kunne ikke oppdatere status' }));
      }
    } catch (err: any) {
      setServiceErrors(prev => ({ ...prev, [serviceName]: err.message }));
    }
  };

  const handleTest = async (serviceName: string) => {
    try {
      setTesting(serviceName);
      clearServiceMessages(serviceName);

      const response = await authenticatedFetch(`/api/admin/api-settings/${serviceName}/test`, {
        method: 'POST'
      });

      const data = await response.json();

      if (data.success) {
        setServiceSuccess(prev => ({ ...prev, [serviceName]: data.message || 'Tilkobling OK!' }));
        setTimeout(() => clearServiceMessages(serviceName), 5000);
      } else {
        setServiceErrors(prev => ({ ...prev, [serviceName]: data.message || 'Test feilet' }));
      }
    } catch (err: any) {
      setServiceErrors(prev => ({ ...prev, [serviceName]: err.message }));
    } finally {
      setTesting(null);
    }
  };

  const handleSendTestSms = async () => {
    if (!testPhoneNumber.trim()) {
      setServiceErrors(prev => ({ ...prev, twilio: 'Skriv inn et telefonnummer' }));
      return;
    }

    try {
      setSendingSms(true);
      clearServiceMessages('twilio');

      const response = await authenticatedFetch('/api/admin/sms/send-test', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ phone_number: testPhoneNumber.trim() })
      });

      const data = await response.json();

      if (data.success) {
        const msg = data.mock
          ? 'SMS i test-modus (ikke sendt)'
          : `Test-SMS sendt til ${testPhoneNumber}`;
        setServiceSuccess(prev => ({ ...prev, twilio: msg }));
        setTimeout(() => clearServiceMessages('twilio'), 5000);
      } else {
        setServiceErrors(prev => ({ ...prev, twilio: data.error || data.message || 'Kunne ikke sende SMS' }));
      }
    } catch (err: any) {
      setServiceErrors(prev => ({ ...prev, twilio: err.message }));
    } finally {
      setSendingSms(false);
    }
  };

  const updateSetting = (serviceName: string, key: string, value: any) => {
    setEditedSettings(prev => ({
      ...prev,
      [serviceName]: {
        ...prev[serviceName],
        [key]: value
      }
    }));
  };

  const toggleShowSecret = (fieldKey: string) => {
    setShowSecrets(prev => ({
      ...prev,
      [fieldKey]: !prev[fieldKey]
    }));
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center p-8">
        <Loader2 className="h-8 w-8 animate-spin text-kf-primary" />
      </div>
    );
  }

  return (
    <div className="space-y-6">
      <div>
        <h2 className="text-2xl font-bold text-gray-900 flex items-center gap-2">
          <Key className="w-6 h-6" />
          API-integrasjoner
        </h2>
        <p className="text-sm text-gray-600 mt-1">
          Administrer API-nøkler for eksterne tjenester
        </p>
      </div>

      {globalError && (
        <div className="bg-red-50 border border-red-200 text-red-800 px-4 py-3 rounded-lg flex items-center gap-2">
          <AlertCircle className="h-5 w-5 flex-shrink-0" />
          <span>{globalError}</span>
        </div>
      )}

      <div className="space-y-6">
        {Object.entries(SERVICE_CONFIGS).map(([key, config]) => {
          const service = services.find(s => s.service_name === key);
          const settings = editedSettings[key] || {};

          return (
            <div key={key} className="bg-white rounded-lg border shadow-sm overflow-hidden">
              {/* Header */}
              <div className="bg-gray-50 px-6 py-4 border-b flex items-center justify-between">
                <div className="flex items-center gap-3">
                  <div className={`p-2 rounded-lg ${service?.is_active ? 'bg-green-100 text-green-600' : 'bg-gray-100 text-gray-500'}`}>
                    {config.icon}
                  </div>
                  <div>
                    <h3 className="font-semibold text-gray-900">{config.title}</h3>
                    <p className="text-sm text-gray-600">{config.description}</p>
                  </div>
                </div>
                <div className="flex items-center gap-3">
                  <a
                    href={config.docsUrl}
                    target="_blank"
                    rel="noopener noreferrer"
                    className="text-sm text-blue-600 hover:text-blue-800 flex items-center gap-1"
                  >
                    Dokumentasjon
                    <ExternalLink className="w-3 h-3" />
                  </a>
                  <label className="relative inline-flex items-center cursor-pointer">
                    <input
                      type="checkbox"
                      className="sr-only peer"
                      checked={service?.is_active || false}
                      onChange={() => handleToggleActive(key)}
                    />
                    <div className="w-11 h-6 bg-gray-200 peer-focus:outline-none peer-focus:ring-4 peer-focus:ring-kf-primary/20 rounded-full peer peer-checked:after:translate-x-full peer-checked:after:border-white after:content-[''] after:absolute after:top-[2px] after:left-[2px] after:bg-white after:border-gray-300 after:border after:rounded-full after:h-5 after:w-5 after:transition-all peer-checked:bg-kf-primary"></div>
                    <span className="ml-2 text-sm text-gray-700">
                      {service?.is_active ? 'Aktiv' : 'Inaktiv'}
                    </span>
                  </label>
                </div>
              </div>

              {/* Fields */}
              <div className="p-6 space-y-4">
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                  {config.fields.map((field) => {
                    const fieldKey = `${key}-${field.key}`;
                    const isSecret = field.type === 'password';
                    const showValue = showSecrets[fieldKey];
                    const currentValue = settings[field.key] || '';

                    return (
                      <div key={field.key} className={field.key === 'redirect_uri' ? 'md:col-span-2' : ''}>
                        <label className="block text-sm font-medium text-gray-700 mb-1">
                          {field.label}
                          {field.required && <span className="text-red-500 ml-1">*</span>}
                        </label>

                        {field.type === 'select' ? (
                          <select
                            value={currentValue}
                            onChange={(e) => updateSetting(key, field.key, e.target.value)}
                            className="w-full px-3 py-2 border rounded-lg focus:ring-2 focus:ring-kf-primary/50 focus:border-kf-primary"
                          >
                            {field.options?.map(opt => (
                              <option key={opt.value} value={opt.value}>
                                {opt.label}
                              </option>
                            ))}
                          </select>
                        ) : (
                          <div className="relative">
                            <input
                              type={isSecret && !showValue ? 'password' : 'text'}
                              value={currentValue}
                              onChange={(e) => updateSetting(key, field.key, e.target.value)}
                              placeholder={field.placeholder}
                              className="w-full px-3 py-2 border rounded-lg focus:ring-2 focus:ring-kf-primary/50 focus:border-kf-primary pr-10"
                            />
                            {isSecret && (
                              <button
                                type="button"
                                onClick={() => toggleShowSecret(fieldKey)}
                                className="absolute right-2 top-1/2 -translate-y-1/2 text-gray-400 hover:text-gray-600"
                              >
                                {showValue ? <EyeOff className="w-5 h-5" /> : <Eye className="w-5 h-5" />}
                              </button>
                            )}
                          </div>
                        )}

                        {field.helpText && (
                          <p className="text-xs text-gray-500 mt-1">{field.helpText}</p>
                        )}
                      </div>
                    );
                  })}
                </div>

                {/* Service-specific messages */}
                {serviceErrors[key] && (
                  <div className="bg-red-50 border border-red-200 text-red-800 px-3 py-2 rounded-lg flex items-center gap-2 text-sm">
                    <AlertCircle className="h-4 w-4 flex-shrink-0" />
                    <span>{serviceErrors[key]}</span>
                  </div>
                )}

                {serviceSuccess[key] && (
                  <div className="bg-green-50 border border-green-200 text-green-800 px-3 py-2 rounded-lg flex items-center gap-2 text-sm">
                    <CheckCircle className="h-4 w-4 flex-shrink-0" />
                    <span>{serviceSuccess[key]}</span>
                  </div>
                )}

                {/* Actions */}
                <div className="flex items-center gap-3 pt-4 border-t">
                  <button
                    onClick={() => handleSave(key)}
                    disabled={saving === key}
                    className="flex items-center gap-2 px-4 py-2 bg-kf-primary text-white rounded-lg hover:bg-kf-primary/90 disabled:opacity-50"
                  >
                    {saving === key ? (
                      <>
                        <Loader2 className="w-4 h-4 animate-spin" />
                        Lagrer...
                      </>
                    ) : (
                      <>
                        <Save className="w-4 h-4" />
                        Lagre
                      </>
                    )}
                  </button>
                  <button
                    onClick={() => handleTest(key)}
                    disabled={testing === key || !service?.is_active}
                    className="flex items-center gap-2 px-4 py-2 bg-gray-100 text-gray-700 rounded-lg hover:bg-gray-200 disabled:opacity-50"
                    title={!service?.is_active ? 'Aktiver tjenesten for å teste' : ''}
                  >
                    {testing === key ? (
                      <>
                        <Loader2 className="w-4 h-4 animate-spin" />
                        Tester...
                      </>
                    ) : (
                      <>
                        <TestTube2 className="w-4 h-4" />
                        Test tilkobling
                      </>
                    )}
                  </button>
                </div>

                {/* SMS Test Section - Only for Twilio */}
                {key === 'twilio' && service?.is_active && (
                  <div className="mt-4 pt-4 border-t border-dashed">
                    <div className="flex items-center gap-2 mb-2">
                      <Phone className="w-4 h-4 text-gray-500" />
                      <span className="text-sm font-medium text-gray-700">Send test-SMS</span>
                    </div>
                    <div className="flex items-center gap-2">
                      <input
                        type="tel"
                        value={testPhoneNumber}
                        onChange={(e) => setTestPhoneNumber(e.target.value)}
                        placeholder="Telefonnummer (f.eks. 12345678)"
                        className="flex-1 px-3 py-2 border rounded-lg focus:ring-2 focus:ring-kf-primary/50 focus:border-kf-primary text-sm"
                      />
                      <button
                        onClick={handleSendTestSms}
                        disabled={sendingSms || !testPhoneNumber.trim()}
                        className="flex items-center gap-2 px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 disabled:opacity-50 text-sm"
                      >
                        {sendingSms ? (
                          <>
                            <Loader2 className="w-4 h-4 animate-spin" />
                            Sender...
                          </>
                        ) : (
                          <>
                            <Send className="w-4 h-4" />
                            Send SMS
                          </>
                        )}
                      </button>
                    </div>
                    <p className="text-xs text-gray-500 mt-1">
                      Tast inn ditt mobilnummer for å motta en test-SMS
                    </p>
                  </div>
                )}

                {/* Last updated */}
                {service?.updated_at && (
                  <p className="text-xs text-gray-500">
                    Sist oppdatert: {new Date(service.updated_at).toLocaleString('nb-NO')}
                  </p>
                )}
              </div>
            </div>
          );
        })}
      </div>

      {/* Security Notice */}
      <div className="bg-amber-50 border border-amber-200 rounded-lg p-4 flex items-start gap-3">
        <AlertCircle className="w-5 h-5 text-amber-600 flex-shrink-0 mt-0.5" />
        <div>
          <h4 className="font-semibold text-amber-900">Sikkerhetsinformasjon</h4>
          <p className="text-sm text-amber-800 mt-1">
            API-nøkler lagres kryptert i databasen. Aldri del disse nøklene med andre.
            Ved mistanke om lekkasje, generer nye nøkler hos leverandøren umiddelbart.
          </p>
        </div>
      </div>
    </div>
  );
}
